// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useState } from 'react';
import { node } from 'prop-types';
import noop from 'lodash/noop';
import { createContext } from 'use-context-selector';
import uniqueId from 'lodash/uniqueId';

export const ToastNotificationsContext = createContext({
  data: null,
  addNotification: noop,
  clearNotification: noop,
  clearAll: noop
});

const ToastNotificationsProvider = memo(({ children }) => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (notifications.length) {
        // keep notifications for 5 seconds
        setNotifications(prevNotifications => {
          const newNotifications = [
            ...prevNotifications.filter(notification => {
              const timeout = Math.max(1000, notification.timeout || 3000);
              return now - notification.timestamp < timeout;
            })
          ];
          if (newNotifications.length < prevNotifications.length) {
            return newNotifications;
          }
          return prevNotifications;
        });
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [notifications]);

  const memoizedValue = useMemo(() => ({
    data: notifications,
    addNotification(payload) {
      if (payload.title || payload.message) {
        setNotifications(prevNotifications => {
          prevNotifications.push(Object.assign(payload, {
            id: uniqueId('toast_'),
            timestamp: Date.now()
          }));
          return [...prevNotifications];
        });
      }
    },
    clearNotification(id) {
      setNotifications(prevNotifications => [
        ...prevNotifications.filter(notification => notification.id !== id)
      ]);
    },
    clearAll() {
      setNotifications([]);
    }
  }), [notifications]);

  return (
    <ToastNotificationsContext.Provider value={ memoizedValue }>
      { children }
    </ToastNotificationsContext.Provider>
  );
});

ToastNotificationsProvider.propTypes = {
  children: node
};
ToastNotificationsProvider.displayName = 'ToastNotificationsProvider';

export default ToastNotificationsProvider;
